<template>
<div :class="['advanced-domain-select', className, `size-${size}`]" >
  <span class="advanced-domain-select__title" v-if="title">{{ title }}</span>
  <v-autocomplete
    ref="autocomplete"
    v-model="domainSelected"
    :items="itemsSorted"
    :size="size"
    :cacheItems='false'
    dense
    append-icon="expand_more"
    outlined
    multiple
    height="40"
    :filter="customFilter"
    :search-input.sync="filterSearch"
    :placeholder="domainSelected.length > 0 ? '' : $t('advanced.select.header.select.domain.cloud.account.inside')"
    :menu-props="{ contentClass: 'advanced-domain-select__content'}"
  >
    <template v-slot:append-outer>
      <custom-tooltip class='tool-tip-hint' placement="right" popper-class="light-tooltip">
        <div slot='buttonContent'>
          <span class="tag" :class='[searchByTag ? "material-icons" : "material-icons-outlined"]' @click.stop='toggleSearchByTag'>
            local_offer
          </span>
        </div>
        <div slot="content">
          <slot name="tooltip">
            <span class='tooltip-text'>{{ $t('advanced.domain.search.by.tag.tool.tip') }}</span>
          </slot>
        </div>
      </custom-tooltip>
    </template>
    <template v-slot:selection="{ index }">
      <span v-if="selectAll && index === 0" class="selection-text">
        {{ domainSelected.length === 1 ? domainSelected[0] : $t('advanced.domain.select.selection.all') }}
      </span>
      <span v-else-if="selectSome && index === 0" class="selection-text">
        {{ domainSelected.length === 1 ? domainSelected[0] : `${$t('advanced.domain.select.selection.selected')} ${domainSelected.length} ${$t('advanced.domain.select.selection.domains')}` }}
      </span>
    </template>
    <template v-slot:prepend-item>
      <div class='list-actions'>
        <v-list-item
          ripple
          @click="toggle"
        >
          <v-list-item-action>
            <v-icon class="material-icons-outlined" :color="domainSelected.length > 0 ? purpleColor : ''">
              {{ icon }}
            </v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>
              {{ $t('advanced.domain.select.all') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item
          class='apply-item'
        >
          <v-list-item-action>
            <v-btn :color='purpleColor' class='apply-btn' @click="apply" @mouseenter='applyMouseEnter' :disabled="!domainSelected.length">
              {{ $t('advanced.domain.select.apply') }}
              <lottie-animation
                class='apply-animation'
                ref="applyAnimation"
                :loop='true'
                :animationData="require('@/assets/icons/animations/ok.json')"
                @loopComplete="loopComplete"
              />
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </div>
      <v-divider class="mt-2"></v-divider>
    </template>
    <template v-slot:item="data">
      <v-list-item
        v-bind="data.attrs"
        ripple
        @click="toggleSingle(data.item)"
      >
        <v-list-item-content>
          <div class='domain-item-row'>
            <v-icon v-if='domainSelected.includes(data.item)' class="material-icons-outlined" :color="domainSelected.includes(data.item) ? purpleColor : ''">
              check_box
            </v-icon>
            <v-icon v-else class="material-icons-outlined" :color="domainSelected.includes(data.item) ? purpleColor : ''">
              check_box_outline_blank
            </v-icon>
            <v-icon class='type-icon'> {{ getIconForItem(data.item) }} </v-icon>
            <div class='domain-item-row__details'>
              <v-list-item-title class='asset-name'>
                {{ data.item }}
              </v-list-item-title>
              <v-list-item-subtitle class='asset-organization'>
                {{ getOrganizationForItem(data.item) }}
              </v-list-item-subtitle>
            </div>
          </div>
        </v-list-item-content>
      </v-list-item>
    </template>
    <template v-slot:append-item>
      <v-divider class="mb-2"></v-divider>
      <v-list-item disabled>
        <v-list-item-avatar color="grey lighten-3">
          <v-icon>
            domain
          </v-icon>
        </v-list-item-avatar>

        <v-list-item-content v-if="selectAll">
          <v-list-item-title>
            {{ $t('advanced.domain.select.all.selected') }}
          </v-list-item-title>
        </v-list-item-content>

        <v-list-item-content v-else-if="selectSome">
          <v-list-item-title>
            {{ $t('advanced.domain.select.total') }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ domainSelected.length }}
          </v-list-item-subtitle>
        </v-list-item-content>

        <v-list-item-content v-else>
          <v-list-item-title>
            {{ $t('advanced.domain.select.no.selected') }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ $t('advanced.domain.select.do.select') }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </template>
  </v-autocomplete>
</div>
</template>

<script>

import { mapGetters } from 'vuex';
import { SET_DOMAIN_FILTER } from '@/store/filters/actionTypes';
import LottieAnimation from 'lottie-web-vue';
import CustomTooltip from '@/components/common/CustomTooltip.vue';

export default {
  name: 'AdvancedDomainSelect',
  props: {
    className: {
      type: String,
      default: () => '',
    },
    title: {
      type: String,
      default: () => '',
    },
    size: {
      type: String,
      default: () => '',
    },
    items: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    LottieAnimation,
    CustomTooltip,
  },
  data() {
    return {
      domainSelected: [],
      domainSelectedSorted: [],
      filterSearch: '',
      searchByTag: false,
    };
  },
  created() {
    this.domainSelected = [...this.domainFilter] || [];
    this.domainSelectedSorted = [...this.domainFilter] || [];
  },
  watch: {
    domainFilter() {
      this.domainSelected = [...this.domainFilter] || [];
      this.domainSelectedSorted = [...this.domainFilter] || [];
    },
    async organizationFilter() {
      this.domainSelected = [];
      this.domainSelectedSorted = [];
    },
  },
  computed: {
    ...mapGetters([
      'domainFilter',
      'domainsTags',
      'domains',
      'companyData',
      'organizationFilter',
      'configurationDomains',
      'configurationCloudAccounts',
    ]),
    ...mapGetters('theme', ['isDarkMode']),
    purpleColor() {
      return this.isDarkMode ? '#B87FD9' : '#8E30D0';
    },
    itemValues() {
      return this.items.map(item => item.value);
    },
    itemsSorted() {
      const itemsCopy = [...this.itemValues];
      if (this.domainSelectedSorted.length) {
        const selectedCopy = [...this.domainSelectedSorted];
        return [...new Set(selectedCopy.sort(this.domainCompare).concat(itemsCopy.sort(this.domainCompare)))];
      }
      return itemsCopy;
    },
    selectAll() {
      return this.domainSelected.length === this.itemsSorted.length;
    },
    selectSome() {
      return this.domainSelected.length > 0 && !this.selectAll;
    },
    selectNone() {
      return this.domainSelected.length <= 0 && !this.selectAll;
    },
    selectAllFromFilter() {
      return this.itemsByFilter.every(item => this.domainSelected.includes(item));
    },
    itemsByFilter() {
      return !this.filterSearch ? this.itemsSorted : this.itemsSorted.filter(item => this.customFilter(item, this.filterSearch, item));
    },
    icon() {
      if (this.selectAll || this.selectAllFromFilter) return 'disabled_by_default';
      if (this.selectSome) return 'indeterminate_check_box';
      return 'check_box_outline_blank';
    },
  },
  methods: {
    getIconForItem(item) {
      const type = this.items.find(fullItem => fullItem.value === item)?.type;
      return type === 'cloud' ? 'cloud' : 'language';
    },
    getOrganizationForItem(item) {
      const org = this.items.find(fullItem => fullItem.value === item)?.organization;
      return org;
    },
    toggleSearchByTag() {
      this.searchByTag = !this.searchByTag;
    },
    applyMouseEnter() {
      this.$refs.applyAnimation.play();
    },
    loopComplete() {
      this.$refs.applyAnimation.pause();
    },
    domainCompare(a, b) {
      if (a === b) return 0;
      return a > b ? 1 : -1;
    },
    async apply() {
      this.domainSelectedSorted = this.domainSelectedSorted.concat(this.domainSelected);
      await this.$emit('apply');
      await this.$store.dispatch(SET_DOMAIN_FILTER, this.domainSelected);
      this.filterSearch = '';
      this.$refs.autocomplete.isMenuActive = false;
    },
    // eslint-disable-next-line no-unused-vars
    customFilter(item, queryText, itemText) {
      if (queryText.length < 1) {
        return true;
      }
      if (this.searchByTag) {
        return Object.keys(this.domainsTags[item])
          .some(tag => this.domainsTags[item][tag] === 'added' && tag.toLocaleLowerCase().indexOf(queryText.toLowerCase()) > -1);
      }
      return itemText.toLocaleLowerCase().indexOf(queryText.toLowerCase()) > -1;
    },
    toggle() {
      if (this.selectAll) {
        this.domainSelected = [];
      } else {
        this.domainSelected = Array.from(new Set([...this.domainSelected, ...this.itemsByFilter]));
      }
    },
    toggleSingle(item) {
      if (this.domainSelected.includes(item)) {
        this.domainSelected.splice(this.domainSelected.indexOf(item), 1);
      } else {
        this.domainSelected.push(item);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/design-system';

.tag {
  color: var(--text-muted);
  font-size: $font-size-xl;

  &:hover {
    cursor: pointer;
  }
}

.tool-tip-hint {
  display: flex;
  align-items: center;
}

.domain-item-row {
  display: flex;
  align-items: center;
  width: 100%;

  .type-icon {
    margin-right: $space-2;
    color: var(--text-secondary);
  }

  .material-icons-outlined {
    margin-right: $space-2;
    color: var(--text-secondary);
  }

  &__details {
    display: flex;
    justify-content: space-between;
    width: 80%;

    .asset-name {
      width: 50%;
      margin-right: 5%;
      color: var(--text-primary);
    }

    .asset-organization {
      display: flex;
      font-size: $font-size-xs;
      justify-content: right;
      width: 40%;
      color: var(--text-secondary);
    }
  }
}

.v-autocomplete__content.v-menu__content .v-select-list {
  position: relative;
}

.list-actions {
  display: flex;
  top: 0;
  position: sticky;
  background-color: var(--bg-secondary);
  z-index: 1;
}

.advanced-domain-select {
  :deep(.v-input) {
    margin-top: 0;
  }

  &__title {
    font-family: $font-family-regular;
    font-size: $font-size-xs;
    letter-spacing: 0.8px;
    line-height: $font-size-xs;
    margin-bottom: $space-1;
    color: var(--text-primary);
  }
}

.selection-text {
  color: var(--text-primary);
  font-family: $font-family-regular;
  font-size: $font-size-base;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

.advanced-domain-select {
  position: relative;

  :deep(.theme--light.v-list-item:not(.v-list-item--disabled) .v-list-item__subtitle) {
    margin-left: 14%;
  }

  // Override Vuetify default margin to position tag icon centered on the input border
  .v-input__append-outer {
    position: absolute;
    right: $space-8;
    top: 50%;
    transform: translateY(-50%);
    margin: 0 !important;
    width: auto;
    display: flex;
    align-items: center;
  }

  // Override Vuetify default margin to vertically center chevron within the input
  .v-input__icon--append {
    display: flex;
    align-items: center;
    margin-top: $space-1 !important;

    .v-icon {
      color: var(--text-muted);
    }
  }

  .v-input__control {
    display: flex;
    align-items: center;
  }

  &.background {
    min-width: rem(350);
    max-width: rem(350);
    margin-top: $space-1;

    .v-select__slot {
      background-color: transparent;
    }
  }

  .v-select__selections {
    margin-left: $space-3;
    padding-right: $space-6;
    flex-wrap: nowrap;

    :first-child {
      max-width: rem(180);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      .selection-text {
        margin-left: $space-2;
      }
    }

    input {
      margin-right: $space-6;
    }
  }

  .v-select__slot {
    background-color: transparent;
  }

  // Override vuetify-overrides outlined input background
  .v-autocomplete.v-autocomplete.v-text-field--outlined .v-input__slot {
    background-color: #ffffff;
  }

  .v-text-field--outlined input {
    padding: 0;
  }
}

.advanced-domain-select__content.v-menu__content {
  min-width: rem(350);
  max-width: rem(350);

  .v-list.v-list {
    background: var(--bg-primary);
  }

  .list-actions {
    background: var(--bg-primary);

    .apply-item {
      background-color: transparent !important;

      &::before {
        display: none !important;
      }

      &:hover {
        background-color: transparent !important;
      }

      .v-list-item__overlay {
        display: none !important;
      }

      display: flex;
      align-items: center;
      justify-content: center;

      .v-list-item__action {
        margin: 0 auto !important;
      }
    }
  }

  // Fix selected row highlight - lighter purple background
  .v-list-item.v-list-item--highlighted,
  .v-list-item.v-list-item--active {
    background-color: rgba(143, 36, 211, 0.1);
  }

  // Be very specific to override Vuetify's default styles
  .v-list-item .v-list-item__content .domain-item-row {
    display: flex;
    align-items: center;
    width: 100%;
    // Domain name should be dark normally
    .asset-name {
      color: var(--text-primary);
    }
    .asset-organization {
      color: var(--text-secondary);
    }

    // Type icons (globe, cloud) should be darker, not light gray
    .v-icon.type-icon {
      color: var(--text-secondary);
    }

    // Checkbox icons should be darker
    .v-icon.material-icons-outlined {
      color: var(--text-secondary);
    }
  }

  // When row is selected/highlighted, domain name should be purple
  .v-list-item--highlighted .v-list-item__content .domain-item-row .asset-name,
  .v-list-item--active .v-list-item__content .domain-item-row .asset-name {
    color: var(--brand-primary);
  }

  // Icons should also be purple when selected
  .v-list-item--highlighted .v-list-item__content .domain-item-row .v-icon.type-icon,
  .v-list-item--active .v-list-item__content .domain-item-row .v-icon.type-icon {
    color: var(--brand-primary);
  }

  // Override Vuetify's list item title/subtitle with full path
  .v-list-item .v-list-item__content .v-list-item__title {
    color: var(--text-primary);
  }

  .v-list-item .v-list-item__content .v-list-item__subtitle {
    color: var(--text-secondary);
  }
}
</style>
